// Updated VendorPropertyDefinitionForm.jsx - Add mapping to category columns
import React, { useState, useEffect } from 'react';

const VendorPropertyDefinitionForm = ({
    existingProperties = [],
    onSave,
    onCancel,
    categoryTableColumns = [] // New prop: available columns from category
}) => {
    const [properties, setProperties] = useState(existingProperties);
    const [editingIndex, setEditingIndex] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [currentProperty, setCurrentProperty] = useState({
        name: '',
        label: '',
        type: 'STRING',
        required: false,
        unit: '',
        placeholder: '',
        defaultValue: '',
        options: [],
        mappedToColumn: '' // New field for mapping
    });

    const propertyTypes = [
        { value: 'STRING', label: 'Text' },
        { value: 'TEXTAREA', label: 'Text Area' },
        { value: 'NUMBER', label: 'Number' },
        { value: 'DATE', label: 'Date' },
        { value: 'BOOLEAN', label: 'Boolean (Yes/No)' },
        { value: 'SELECT', label: 'Dropdown Select' },
        { value: 'MULTISELECT', label: 'Multi-Select' }
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCurrentProperty(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...currentProperty.options];
        newOptions[index] = { ...newOptions[index], [field]: value };
        setCurrentProperty(prev => ({ ...prev, options: newOptions }));
    };

    const addOption = () => {
        setCurrentProperty(prev => ({
            ...prev,
            options: [...prev.options, { value: '', label: '' }]
        }));
    };

    const removeOption = (index) => {
        const newOptions = currentProperty.options.filter((_, i) => i !== index);
        setCurrentProperty(prev => ({ ...prev, options: newOptions }));
    };

    const saveProperty = () => {
        if (!currentProperty.name || !currentProperty.label) {
            alert('Name and Label are required');
            return;
        }

        if (!currentProperty.mappedToColumn) {
            alert('Please map this property to a category column');
            return;
        }

        const isDuplicate = properties.some((prop, idx) =>
            prop.name === currentProperty.name && idx !== editingIndex
        );

        if (isDuplicate) {
            alert(`Property with name "${currentProperty.name}" already exists`);
            return;
        }

        if (editingIndex !== null) {
            const updatedProperties = [...properties];
            updatedProperties[editingIndex] = currentProperty;
            setProperties(updatedProperties);
            setEditingIndex(null);
        } else {
            setProperties([...properties, currentProperty]);
        }

        setCurrentProperty({
            name: '',
            label: '',
            type: 'STRING',
            required: false,
            unit: '',
            placeholder: '',
            defaultValue: '',
            options: [],
            mappedToColumn: ''
        });
        setShowForm(false);
    };

    const editProperty = (index) => {
        setCurrentProperty(properties[index]);
        setEditingIndex(index);
        setShowForm(true);
    };

    const deleteProperty = (index) => {
        if (window.confirm('Are you sure you want to delete this property?')) {
            const updatedProperties = properties.filter((_, i) => i !== index);
            setProperties(updatedProperties);
            if (editingIndex === index) {
                setEditingIndex(null);
                setShowForm(false);
            }
        }
    };

    const handleSaveAll = () => {
        onSave(properties);
    };

    // Get available columns that haven't been mapped yet
    const getAvailableColumns = () => {
        const mappedColumns = properties.map(p => p.mappedToColumn).filter(Boolean);
        return categoryTableColumns.filter(col =>
            col.isVisible && !mappedColumns.includes(col.header)
        );
    };

    return (
        <div className="vendor-property-definition-form">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Vendor Item Properties</h6>
                {!showForm && (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => setShowForm(true)}
                    >
                        + Add Property
                    </button>
                )}
            </div>

            {showForm && (
                <div className="card mb-3 p-3 bg-light">
                    <h6>{editingIndex !== null ? 'Edit Property' : 'Add New Property'}</h6>

                    <div className="row">
                        <div className="col-md-6 mb-2">
                            <label className="form-label small">Property Name *</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                name="name"
                                value={currentProperty.name}
                                onChange={handleInputChange}
                                placeholder="e.g., bookName"
                                disabled={editingIndex !== null}
                            />
                            <small className="text-muted">Unique identifier (no spaces)</small>
                        </div>

                        <div className="col-md-6 mb-2">
                            <label className="form-label small">Display Label *</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                name="label"
                                value={currentProperty.label}
                                onChange={handleInputChange}
                                placeholder="e.g., Book Name"
                            />
                        </div>

                        <div className="col-md-4 mb-2">
                            <label className="form-label small">Field Type</label>
                            <select
                                className="form-select form-select-sm"
                                name="type"
                                value={currentProperty.type}
                                onChange={handleInputChange}
                            >
                                {propertyTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-4 mb-2">
                            <label className="form-label small">Map to Category Column *</label>
                            <select
                                className="form-select form-select-sm"
                                name="mappedToColumn"
                                value={currentProperty.mappedToColumn}
                                onChange={handleInputChange}
                            >
                                <option value="">Select column...</option>
                                {getAvailableColumns().map(col => (
                                    <option key={col.header} value={col.header}>
                                        {col.header}
                                    </option>
                                ))}
                                {/* Also show already mapped column when editing */}
                                {editingIndex !== null && currentProperty.mappedToColumn && (
                                    <option value={currentProperty.mappedToColumn}>
                                        {currentProperty.mappedToColumn} (currently mapped)
                                    </option>
                                )}
                            </select>
                            <small className="text-muted">Which table column this property belongs to</small>
                        </div>

                        <div className="col-md-4 mb-2">
                            <label className="form-label small">Unit (Optional)</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                name="unit"
                                value={currentProperty.unit}
                                onChange={handleInputChange}
                                placeholder="e.g., kg, meters"
                            />
                        </div>

                        <div className="col-md-6 mb-2">
                            <label className="form-label small">Placeholder</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                name="placeholder"
                                value={currentProperty.placeholder}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="col-md-6 mb-2">
                            <div className="form-check mt-4">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    name="required"
                                    checked={currentProperty.required}
                                    onChange={handleInputChange}
                                    id="requiredCheck"
                                />
                                <label className="form-check-label" htmlFor="requiredCheck">
                                    Required Field
                                </label>
                            </div>
                        </div>
                    </div>

                    {(currentProperty.type === 'SELECT' || currentProperty.type === 'MULTISELECT') && (
                        <div className="mt-3">
                            <label className="form-label small">Options</label>
                            <div className="border rounded p-2 bg-white">
                                {currentProperty.options.map((option, idx) => (
                                    <div key={idx} className="row mb-2">
                                        <div className="col-md-5">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Value"
                                                value={option.value}
                                                onChange={(e) => handleOptionChange(idx, 'value', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-5">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Label"
                                                value={option.label}
                                                onChange={(e) => handleOptionChange(idx, 'label', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger"
                                                onClick={() => removeOption(idx)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={addOption}
                                >
                                    + Add Option
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mt-3">
                        <button
                            type="button"
                            className="btn btn-sm btn-success me-2"
                            onClick={saveProperty}
                        >
                            {editingIndex !== null ? 'Update' : 'Add'} Property
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => {
                                setShowForm(false);
                                setEditingIndex(null);
                                setCurrentProperty({
                                    name: '',
                                    label: '',
                                    type: 'STRING',
                                    required: false,
                                    unit: '',
                                    placeholder: '',
                                    defaultValue: '',
                                    options: [],
                                    mappedToColumn: ''
                                });
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {properties.length > 0 && (
                <div className="mt-3">
                    <h6 className="mb-2">Defined Properties ({properties.length})</h6>
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                            <thead className="table-secondary">
                                <tr>
                                    <th>Name</th>
                                    <th>Label</th>
                                    <th>Type</th>
                                    <th>Mapped To</th>
                                    <th>Required</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {properties.map((prop, idx) => (
                                    <tr key={idx}>
                                        <td><code>{prop.name}</code></td>
                                        <td>{prop.label}</td>
                                        <td>{prop.type}</td>
                                        <td><span className="badge bg-info">{prop.mappedToColumn || 'Not mapped'}</span></td>
                                        <td>{prop.required ? 'Yes' : 'No'}</td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-warning me-1"
                                                onClick={() => editProperty(idx)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => deleteProperty(idx)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {properties.length > 0 && (
                <div className="mt-3">
                    <button className="btn btn-primary" onClick={handleSaveAll}>
                        Save All Properties
                    </button>
                </div>
            )}
        </div>
    );
};

export default VendorPropertyDefinitionForm;